// Dependencies
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useClickAway, useEvent } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';

// Styled Components
import { AnimatedModal, Backdrop } from './styles';

// Types
import { BackDropContainerProps } from './types';

// Actions
import { closedModal } from '../../../../state/actions/ui/modal';

// Selectors
import { getCurrentModal } from '../../../../state/selectors/ui/modal';

export default function BaseModal({
  children,
  handleClickClose,
}: BackDropContainerProps) {
  // Redux
  const dispatch = useDispatch();
  const currentModal = useSelector(getCurrentModal, isEqual);

  const isOpen = useMemo(
    () => !!(currentModal && currentModal.id),
    [currentModal]
  );

  // Context
  const modalRef = useRef(null);

  /**
   * @function handleKeyDown():
   * @description Catch the keyboard press event.
   *
   * 🔴 La lista de dependencias NO puede estar vacía. Este contenedor se monta
   * UNA vez y no se desmonta entre modales (`SuperModal` lo renderiza siempre,
   * con o sin modal abierto), así que con `[]` se congelaba el
   * `handleClickClose` del PRIMER render — el de cuando no había ningún modal.
   * Y ese cierra siempre, porque sus guardas se preguntaban por un modal que
   * entonces no existía.
   *
   * Consecuencia: la tecla Escape cerraba también los modales que existen
   * justamente para no tener salida — `GPS_REQUIRED`,
   * `BACKGROUND_LOCATION_REQUIRED` y la actualización marcada como obligatoria.
   * El clic fuera sí los respetaba, porque `useClickAway` recibe la función
   * viva. Cazado al escribir el E2E de la puerta de GPS (2026-08-24).
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClickClose();
      }
    },
    [handleClickClose]
  );

  useEvent('keydown', handleKeyDown);

  useClickAway(modalRef, (event) => {
    // Tocar un aviso flotante (toast) no es tocar fuera: los avisos se pintan
    // POR ENCIMA del modal y se cierran al tocarlos. Ese toque cerraba el
    // modal de debajo, y con la cámara tiraba sin avisar las fotos aún sin
    // entregar (en un recorrido salen avisos solos: GPS, errores de red…).
    const target = event.target as Element | null;
    if (target?.closest?.('.Toastify')) return;
    if (isOpen) {
      handleClickClose();
    }
  });

  useEffect(() => {
    return () => {
      dispatch(closedModal());
    };
  }, [isOpen, dispatch]);

  return (
    <Backdrop visible={isOpen}>
      <AnimatedModal ref={modalRef} isOpen={isOpen}>
        {children}
      </AnimatedModal>
    </Backdrop>
  );
}
